import { FC, Fragment, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	Radio,
	Result,
	Row,
	Select,
	Table,
	Tabs,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import { FileSearchOutlined, UploadOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow from '@/libs/antd/components/DataRow';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import {
	CreditAnalysisResultPayload,
	getProspectCreditCheck,
	getSlikRobotikDetail,
	getSlikRobotikResult,
	submitCreditAnalysisResult,
} from '@/libs/fleet-api/services/prospect';
import formRule from '@/libs/antd/rules';
import FleetModal from '@/libs/antd/components/Modal';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useApp } from '@/App';
import RejectModal, {
	useRejectModal,
} from '../../fbdh/customer-checking/RejectModal';
import NegativeModal, {
	useNegativeModal,
} from '../../fbdh/customer-checking/NegativeModal';
import { uploader } from '@/libs/fleet-api/uploader';
import { FilePayload } from '@/libs/fleet-api/services/customer';
import fetcher, { FleetFile } from '@/libs/fleet-api/fetcher';
import { LoadingOutlined } from '@ant-design/icons';
import { FileData } from '@/libs/fleet-api/services/types';
import { useSelectSlikStatus } from '@/libs/fleet-api/hooks/master-data';
import dayjs from 'dayjs';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import rule from '@/libs/antd/rules';
import AnalisaSLIKFormRobotik from './AnalisaSLIKForm.robotik';

export type CCAAnalisaSLIKFormProps = {
	prospectId: string;
};

const FAQ_TITLE = {
	'dukcapil': 'DUKCAPIL',
	'dttot': 'Daftar Terduga Teroris dan Organisasi Teroris (DTTOT)',
	'dppspm': 'Daftar Pendanaan Proliferasi Senjata Pemusnah Massal (DPPSPM)',
	'online-betting': 'Daftar Pihak/Rekening Terkait Judi Online',
	'pep':
		'Analisa EDD (Enhanced Due Dilligent) High Risk & Politically Exposed Person (PEP)',
};

const MAP_ANALYSIS = {
	'dukcapil': 'dukcapilAnalysis',
	'dttot': 'dttotAnalysis',
	'dppspm': 'dppspmAnalysis',
	'online-betting': 'onlineBettingAnalysis',
	'pep': 'pepAnalysis',
};

const CATEGORY_LABEL = {
	'dukcapil': 'DUKCAPIL',
	'dttot': 'DTTOT',
	'dppspm': 'DPPSPM',
	'online-betting': 'Judi Online',
	'pep': 'PEP',
};

type CreditAnalysisForm = Omit<CreditAnalysisResultPayload, 'slikFile'> & {
	slikFile: FileData;
	slikResult: string;
};

const CCAAnalisaSLIKForm: FC<CCAAnalisaSLIKFormProps> = ({ prospectId }) => {
	// Placeholder
	// const isSlikRobotik = true;

	const { navigate, notification, message } = useApp();
	const {
		modal,
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openProspectDetail,
		openManagementModal,
		openShareholderModal,
		openPreviewModal,
		openRejectHistoryModal,
		openNegativeHistoryModal,
	} = useModals();

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectCreditCheck', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCreditCheck(id);
		},
	});

	// get result slik robotik
	const { data: slikRobotikResult, isLoading: isLoadingSlikRobotikResult } =
		useQuery({
			queryKey: ['getSlikRobotikResult', prospectId] as const,
			queryFn: ({ queryKey }) => {
				const [, id] = queryKey;
				return getSlikRobotikResult(id);
			},
			enabled: !!prospectId,
		});

	const { isRobotic: isSlikRobotik, isRoboticFailed } =
		data?.data.slikInquiryMetric ?? {};

	const isJF = data?.data.mainInformation?.customerType?.id === 1;

	// const isSlikRobotik = true;

	// get result detail robotik
	const { data: slikRobotikDetail, error: isErrorSlikRobotikDetail } = useQuery(
		{
			queryKey: ['getSlikRobotikDetail', prospectId] as const,
			queryFn: ({ queryKey }) => {
				const [, id] = queryKey;
				return getSlikRobotikDetail(id);
			},
			enabled: isSlikRobotik,
		},
	);

	const {
		mainInformation,
		managementSlikInformation,
		shareholderSlikInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		creditExternalChecks,
		negativeHistoryAnalysis,
		rejectedHistoryAnalysis,
		customerRejectionHistory,
		customerNegativeHistory,
		slikFile,
		slikCheckingDate,
		slikCheckingStatus,
		isRecheckSlik,
	} = data?.data || {};

	// const noSlikChecking =
	// 	(isJF &&
	// 		slikCheckingStatus?.id === 1 &&
	// 		dayjs().diff(dayjs(slikCheckingDate), 'day') <= 90) ||
	// 	!isRecheckSlik;

	const { form, fieldName } = useForm<CreditAnalysisForm>();

	const pepPengurus = Form.useWatch(['answers', 9, 'answer'], form);
	const pepShareholder = Form.useWatch(['answers', 10, 'answer'], form);

	const submitResult = useMutation({ mutationFn: submitCreditAnalysisResult });

	const [successModal, setSuccessModal] = useState(false);
	const [rejectModal, setRejectModal] = useRejectModal();
	const [negativeModal, setNegativeModal] = useNegativeModal();
	const [activeTab, setActiveTab] = useState<string>('Internal Checking');

	const [slikCheckActiveTab, setSlikCheckActiveTab] = useState<
		'manager' | 'shareholder'
	>('manager');

	const slikStatus = useSelectSlikStatus();

	const getAnalysisLabel = (category: string) => {
		return (
			CATEGORY_LABEL[category as keyof typeof CATEGORY_LABEL] ??
			category.toUpperCase()
		);
	};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const showSlikManual =
		!isSlikRobotik ||
		isRoboticFailed ||
		isErrorSlikRobotikDetail ||
		!slikRobotikDetail?.data?.[0]?.RPT_SUMMARY_COMPANY?.length;

	// Check if robotic SLIK is done but details are empty
	const isRoboticDoneButEmpty =
		isSlikRobotik &&
		!isRoboticFailed &&
		!isErrorSlikRobotikDetail &&
		slikRobotikResult?.data?.status === 'DONE' &&
		(!slikRobotikDetail?.data || slikRobotikDetail.data.length === 0);

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	// Show error page if robotic SLIK is done but details are empty
	if (isRoboticDoneButEmpty) {
		try {
			const cbasIds =
				slikRobotikResult?.data?.details
					?.map((detail: { cbasId: string }) => detail.cbasId)
					.filter(Boolean) || [];

			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '400px',
						padding: '40px',
						textAlign: 'center',
					}}
				>
					<Result
						status="warning"
						title={
							<div style={{ fontSize: 24, marginBottom: 16 }}>
								Data SLIK Belum Tersedia
							</div>
						}
						subTitle={
							<div>
								<p style={{ marginBottom: 20, fontSize: 16 }}>
									Slik robotik detail belum tersedia dari CBAS. Mohon crosscek
									dengan referensi CBAS ID:
								</p>
								{cbasIds.length > 0 ? (
									<Table
										dataSource={slikRobotikResult.data.details
											.map((detail: { cbasId: string }) => detail.cbasId)
											.filter(Boolean)
											.map((cbasId: string, index: number) => {
												const detailInfo = slikRobotikResult.data.details.find(
													(d: { cbasId: string; referenceNumber?: string }) =>
														d.cbasId === cbasId,
												);
												return {
													key: index,
													no: index + 1,
													cbasId,
													referenceNumber: detailInfo?.referenceNumber || 'N/A',
												};
											})}
										columns={[
											{
												title: 'No',
												dataIndex: 'no',
												key: 'no',
												width: 60,
												align: 'center',
											},
											{
												title: 'CBAS ID',
												dataIndex: 'cbasId',
												key: 'cbasId',
												align: 'center',
											},
											{
												title: 'Reference Number',
												dataIndex: 'referenceNumber',
												key: 'referenceNumber',
												align: 'center',
											},
										]}
										size="small"
										pagination={false}
										bordered
										style={{ marginTop: '8px' }}
									/>
								) : (
									<p style={{ color: '#666', marginTop: '16px' }}>
										Tidak ada referensi CBAS ID yang tersedia
									</p>
								)}
							</div>
						}
					/>
				</div>
			);
		} catch (error) {
			console.error('Error rendering SLIK empty data page:', error);
			// Fallback to simple error message to prevent Sentry crash
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '400px',
						padding: '40px',
						textAlign: 'center',
					}}
				>
					<h2 style={{ color: '#faad14', marginBottom: '16px' }}>
						Data SLIK Belum Tersedia
					</h2>
					<p style={{ color: '#666', marginBottom: '20px' }}>
						Slik robotik detail belum tersedia dari CBAS.
					</p>
					<p style={{ color: '#666', marginBottom: '16px' }}>
						Mohon crosscek dengan referensi CBAS ID:
					</p>
					{slikRobotikResult?.data?.details &&
						slikRobotikResult.data.details.length > 0 && (
							<Table
								dataSource={slikRobotikResult.data.details
									.map((detail: { cbasId: string }) => detail.cbasId)
									.filter(Boolean)
									.map((cbasId: string, index: number) => {
										const detailInfo = slikRobotikResult.data.details.find(
											(d: { cbasId: string; referenceNumber?: string }) =>
												d.cbasId === cbasId,
										);
										return {
											key: index,
											no: index + 1,
											cbasId,
											referenceNumber: detailInfo?.referenceNumber || 'N/A',
										};
									})}
								columns={[
									{
										title: 'No',
										dataIndex: 'no',
										key: 'no',
										width: 60,
										align: 'center',
									},
									{
										title: 'CBAS ID',
										dataIndex: 'cbasId',
										key: 'cbasId',
										align: 'center',
									},
									{
										title: 'Reference Number',
										dataIndex: 'referenceNumber',
										key: 'referenceNumber',
										align: 'center',
									},
								]}
								size="small"
								pagination={false}
								bordered
								style={{ marginTop: '8px' }}
							/>
						)}
					<p style={{ color: '#666', marginTop: '20px' }}>
						Mohon hubungi administrator untuk informasi lebih lanjut.
					</p>
				</div>
			);
		}
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.value}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						negativeHistoryAnalysis: negativeHistoryAnalysis?.analysisResult,
						rejectedHistoryAnalysis: rejectedHistoryAnalysis?.analysisResult,
						dukcapilAnalysis: creditExternalChecks?.find(
							(record) => record.category === 'dukcapil',
						)?.analysisResult,
						dttotAnalysis: creditExternalChecks?.find(
							(record) => record.category === 'dttot',
						)?.analysisResult,
						dppspmAnalysis: creditExternalChecks?.find(
							(record) => record.category === 'dttot',
						)?.analysisResult,
						pepAnalysis: creditExternalChecks?.find(
							(record) => record.category === 'pep',
						)?.analysisResult,
						slikFile: slikFile && {
							id: String(slikFile.id),
							name: slikFile.name,
							path: slikFile.path,
							url: slikFile.url,
							format: slikFile.format,
						},
						prospectId: mainInformation?.prospectId,
						managementSlikInformation: managementSlikInformation?.map(
							(record) => {
								return {
									id: record.id,
									slikResult: record.slikResult,
									idebFile:
										record.idebFile &&
										({
											id: String(record.idebFile.id),
											name: record.idebFile.name,
											path: record.idebFile.path!,
											format: record.idebFile.format,
											url: record.idebFile.url,
										} as FilePayload),
								};
							},
						),
						shareholderSlikInformation: shareholderSlikInformation?.map(
							(record) => {
								return {
									id: record.id,
									slikResult: record.slikResult,
									idebFile:
										record.idebFile &&
										({
											id: String(record.idebFile.id),
											name: record.idebFile.name,
											path: record.idebFile.path!,
											format: record.idebFile.format,
											url: record.idebFile.url,
										} as FilePayload),
								};
							},
						),
						slikResult: mainInformation?.slikResult,
					} as CreditAnalysisForm
				}
				onFinish={async (values) => {
					const isConfirmed = await modal.confirm({
						title: 'Anda yakin akan menyimpan data ini?',
					});
					if (!isConfirmed) return;
					try {
						const payload = {
							prospectId: mainInformation!.prospectId,
							notes: values.notes,
							dppspmAnalysis: values.dppspmAnalysis,
							dttotAnalysis: values.dttotAnalysis,
							dukcapilAnalysis: values.dukcapilAnalysis,
							onlineBettingAnalysis: values.onlineBettingAnalysis,
							pepAnalysis: values.pepAnalysis,
							negativeHistoryAnalysis: values.negativeHistoryAnalysis,
							rejectedHistoryAnalysis: values.rejectedHistoryAnalysis,
							slikFile: values?.slikFile
								? {
										id: values.slikFile.id,
										name: values.slikFile.name,
										path: values.slikFile.path,
									}
								: undefined,
							slikResult: values.slikResult ?? undefined,
							answers:
								values.answers
									?.filter((answer) => answer)
									.map((answer) => {
										return {
											id: answer.id,
											answer: answer.answer,
											evidenceFile: answer.evidenceFile
												? {
														id: answer.evidenceFile.id,
														name: answer.evidenceFile.name,
														path: answer.evidenceFile.path,
													}
												: undefined,
										};
									}) ?? [],
							managementSlikInformation:
								values.managementSlikInformation
									?.filter((management) => management)
									.map((item) => {
										return {
											id: item.id,
											slikResult: item.slikResult,
											idebFile:
												item.idebFile && showSlikManual
													? {
															id: String(item.idebFile.id),
															name: item.idebFile.name,
															path: item.idebFile.path!,
														}
													: undefined,
										};
									}) ?? [],
							shareholderSlikInformation:
								values.shareholderSlikInformation
									?.filter((shareholder) => shareholder)
									.map((item) => {
										return {
											id: item.id,
											slikResult: item.slikResult,
											idebFile:
												item.idebFile && showSlikManual
													? {
															id: String(item.idebFile.id),
															name: item.idebFile.name,
															path: item.idebFile.path!,
														}
													: undefined,
										};
									}) ?? [],
							isSlikRobotik: values.isSlikRobotik ?? false,
							selectedCompanyIndexes: values.selectedCompanyIndexes ?? [],
							selectedShareholderIndexes:
								values.selectedShareholderIndexes ?? [],
						};

						const { data } = await submitResult.mutateAsync(payload);
						if (data) {
							notification.success({
								message: 'Success',
								description: 'Data berhasil disimpan',
							});
							navigate('CCAAnalisaSLIKPage');
						}
					} catch (error) {
						setSuccessModal(false);
						console.log('error', error);
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages &&
										message?.metadata?.debugInfo?.messages.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				<Tabs
					activeKey={activeTab}
					onChange={(key) => setActiveTab(key)}
					items={[
						{
							label: 'Internal Checking',
							disabled: true,
							key: 'Internal Checking',
							children: (
								<Fragment>
									<FleetTitle>Reject Customer History</FleetTitle>
									<Table
										scroll={{ x: 'max-content' }}
										style={{
											marginBottom: 20,
										}}
										bordered
										loading={isLoading}
										pagination={false}
										dataSource={
											customerRejectionHistory?.map((reject, key) => {
												return {
													key,
													...reject,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'No',
												render: (_, record) => record.no,
											},
											{
												title: 'Prospect ID',
												render: (_, record) => record.prospectId,
											},
											{
												title: 'Customer Name',
												render: (_, record) => record.customerName,
											},
											{
												title: 'Reject Status',
												align: 'center',
												render: (_, record) => record.rejectedStatus,
											},
											{
												title: 'Reject Date',
												align: 'center',
												render: (_, record) =>
													record.rejectedDate
														? dayjs(record.rejectedDate).format('DD/MM/YYYY')
														: '-',
											},
											{
												title: 'Rejected Process',
												align: 'center',
												render: (_, record) => record.rejectedProcess,
											},
											{
												title: 'Rejected By',
												align: 'center',
												render: (_, record) => record.rejectedBy,
											},
											{
												title: 'Detail Reject',
												align: 'center',
												render: (_, record) => (
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();
															openRejectHistoryModal(record.rejectedId!);
														}}
													>
														<FileSearchOutlined />
													</a>
												),
											},
										]}
									/>

									<Row
										gutter={[40, 20]}
										style={{ marginBottom: 30 }}
									>
										<Col span={24}>
											<Form.Item
												name="rejectedHistoryAnalysis"
												label="Analisa Reject History"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>

									<FleetTitle>Negative Customer History</FleetTitle>
									<Table
										style={{
											marginBottom: 20,
										}}
										bordered
										scroll={{ x: 'max-content' }}
										loading={isLoading}
										pagination={false}
										dataSource={
											customerNegativeHistory?.map((negative, key) => {
												return {
													key,
													...negative,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'No',
												render: (_, record) => record.no,
											},
											{
												title: 'Prospect ID',
												render: (_, record) => record.prospectId ?? '-',
											},
											{
												title: 'Customer Name',
												render: (_, record) => record.customerName,
											},
											{
												title: 'Negative Status',
												align: 'center',
												render: (_, record) =>
													record.negativeStatus
														? record.negativeStatus === 'Bad' ||
															record.negativeStatus === 'Warning'
															? 'Yes'
															: 'No'
														: '-',
											},
											{
												title: 'Type',
												align: 'center',
												render: (_, record) => (
													<span
														className={`negative-badge status-${record.negativeType?.toLowerCase()}`}
													>
														{record.negativeType ?? '-'}
													</span>
												),
											},
											{
												title: 'Negative Date',
												align: 'center',
												render: (_, record) =>
													record.negativeDate
														? dayjs(record.negativeDate).format('DD/MM/YYYY')
														: '-',
											},
											{
												title: 'Source',
												align: 'center',
												render: (_, record) => record.source ?? '-',
											},
											{
												title: 'Active',
												align: 'center',
												render: (_, record) =>
													record.isActive === 'Active' ? 'Yes' : 'No',
											},
											{
												title: 'Detail Negative',
												align: 'center',
												render: (_, record) => (
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();

															openNegativeHistoryModal(record);
															// setNegativeModal(true);
														}}
													>
														<FileSearchOutlined />
													</a>
												),
											},
										]}
									/>
									<Row
										gutter={[40, 20]}
										style={{ marginBottom: 30 }}
									>
										<Col span={24}>
											<Form.Item
												name="negativeHistoryAnalysis"
												label="Analisa Negative History"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>
									<Button
										type="primary"
										loading={submitResult.isPending || uploadMutation.isPending}
										style={{
											width: 200,
											display: 'block',
											marginLeft: 'auto',
											marginTop: 60,
										}}
										onClick={() => {
											form
												?.validateFields([
													'rejectedHistoryAnalysis',
													'negativeHistoryAnalysis',
												])
												.then(() => {
													setActiveTab('SLIK Checking');
													window.scrollTo({
														top: 0,
														left: 0,
													});
												})
												.catch((err) => {
													console.log('err', err);
												});
										}}
									>
										NEXT
									</Button>
								</Fragment>
							),
						},
						{
							label: 'SLIK Checking',
							disabled: true,
							key: 'SLIK Checking',
							children: showSlikManual ? (
								<Fragment>
									<Typography.Title level={3}>
										SLIK Information
									</Typography.Title>

									<Form.Item
										label="SLIK Robotik"
										name="isSlikRobotik"
										initialValue={!showSlikManual}
										noStyle
										shouldUpdate
									/>

									{isJF &&
									slikCheckingStatus?.id === 1 &&
									slikCheckingDate &&
									dayjs().diff(slikCheckingDate, 'day') <= 90 &&
									!isRecheckSlik ? (
										<Fragment>
											<Row gutter={[40, 20]}>
												<Col span={12}>
													<Form.Item label="View Dokumen CES">
														<Button
															type="link"
															onClick={() => {
																const cesDocument =
																	customerReferralDocuments?.find((doc) =>
																		doc.documentType.includes(
																			'Customer Executive Summary',
																		),
																	);

																if (!cesDocument) return;
																openPreviewModal(cesDocument.file);
															}}
														>
															Dokumen-CES.pdf
														</Button>
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item
														label="SLIK Result"
														name={fieldName(['slikResult'])}
														rules={[formRule.required()]}
													>
														<Select
															allowClear
															options={Array(5)
																.fill(() => null)
																.map((_, i) => {
																	const value = i + 1;
																	return {
																		label: value.toString(),
																		value,
																	};
																})}
														/>
													</Form.Item>
												</Col>
											</Row>
											<Divider />
											<Tabs
												activeKey={slikCheckActiveTab}
												onChange={(key) => {
													setSlikCheckActiveTab(
														key as typeof slikCheckActiveTab,
													);
												}}
												items={[
													{
														key: 'manager',
														label: 'Pengurus',
														children: (
															<Table
																bordered
																style={{ marginBottom: 20 }}
																pagination={false}
																dataSource={
																	managementSlikInformation?.map(
																		(manager, key) => {
																			return {
																				key,
																				...manager,
																			};
																		},
																	) ?? []
																}
																columns={[
																	{
																		title: 'View Detail',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'View Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Upload Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<UploadOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Nama Pemegang Saham',
																		width: 300,
																		render: (_, record) => record.name,
																	},
																	{
																		title: 'SLIK Result',
																		render: (_, record) => {
																			return (
																				<Fragment>
																					<Form.Item
																						name={fieldName([
																							'managementSlikInformation',
																							record.id,
																							'id',
																						])}
																						initialValue={record.id}
																						noStyle
																					/>
																					<Form.Item
																						name={fieldName([
																							'managementSlikInformation',
																							record.id,
																							'slikResult',
																						])}
																						rules={[rule.required()]}
																					>
																						<Select
																							allowClear
																							size="small"
																							style={{ width: 80 }}
																							options={Array(5)
																								.fill(() => null)
																								.map((_, i) => {
																									const value = i + 1;
																									return {
																										label: value.toString(),
																										value,
																									};
																								})}
																						/>
																					</Form.Item>
																				</Fragment>
																			);
																		},
																	},
																]}
															/>
														),
													},
													{
														key: 'shareholder',
														label: 'Pemegang Saham',
														children: (
															<Table
																bordered
																style={{ marginBottom: 20 }}
																pagination={false}
																dataSource={
																	shareholderSlikInformation?.map(
																		(shareholder, key) => {
																			return {
																				key,
																				...shareholder,
																			};
																		},
																	) ?? []
																}
																columns={[
																	{
																		title: 'View Detail',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'View Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Upload Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<UploadOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Nama Pemegang Saham',
																		width: 300,
																		render: (_, record) => record.name,
																	},
																	{
																		title: 'SLIK Result',
																		render: (_, record) => {
																			return (
																				<Fragment>
																					<Form.Item
																						name={fieldName([
																							'shareholderSlikInformation',
																							record.id,
																							'id',
																						])}
																						initialValue={record.id}
																						noStyle
																					/>
																					<Form.Item
																						name={fieldName([
																							'shareholderSlikInformation',
																							record.id,
																							'slikResult',
																						])}
																						rules={[rule.required()]}
																					>
																						<Select
																							allowClear
																							size="small"
																							style={{ width: 80 }}
																							options={Array(5)
																								.fill(() => null)
																								.map((_, i) => {
																									const value = i + 1;
																									return {
																										label: value.toString(),
																										value,
																									};
																								})}
																						/>
																					</Form.Item>
																				</Fragment>
																			);
																		},
																	},
																]}
															/>
														),
													},
												]}
											/>
										</Fragment>
									) : (
										<Fragment>
											{isSlikRobotik ? (
												<Alert
													message="Prospect ini menggunakan SLIK robotik namun terdapat kendala pada sistem. Silakan unggah file SLIK PDF sebagai alternatif."
													type="info"
													showIcon
													style={{ marginBottom: 20 }}
												/>
											) : (
												<Alert
													message="Maaf, sistem robotik sedang dalam proses pemeliharaan. Silakan unggah file SLIK PDF sebagai alternatif."
													type="info"
													showIcon
													style={{ marginBottom: 20 }}
												/>
											)}

											<Form.Item
												label="SLIK Robotik"
												name="isSlikRobotik"
												initialValue={!showSlikManual}
												noStyle
												shouldUpdate
											/>
											{isRecheckSlik && isJF && (
												<Row
													gutter={[40, 20]}
													style={{ marginBottom: 20 }}
												>
													<Col span={12}>
														<Form.Item label="View Dokumen CES">
															<Button
																type="link"
																onClick={() => {
																	const cesDocument =
																		customerReferralDocuments?.find((doc) =>
																			doc.documentType.includes(
																				'Customer Executive Summary',
																			),
																		);

																	if (!cesDocument) return;
																	openPreviewModal(cesDocument.file);
																}}
															>
																Dokumen-CES.pdf
															</Button>
														</Form.Item>
													</Col>
												</Row>
											)}

											<Row gutter={[40, 20]}>
												<Col span={12}>
													<Form.Item
														label="Upload SLIK"
														name={fieldName(['slikFile'])}
														rules={[formRule.required()]}
														getValueProps={(value) => {
															const file = value as FileData;
															return {
																fileList: file?.id
																	? ([
																			{
																				uid: file.id,
																				name: file.name,
																				thumbUrl: file.url,
																			},
																		] as UploadFile[])
																	: [],
															};
														}}
														normalize={({ file }: { file: UploadFile }) => {
															return {
																id: file.uid,
																format: file.type,
																name: file.name,
																url: file.thumbUrl,
															} as FileData;
														}}
													>
														<Upload.Dragger
															listType="picture"
															maxCount={1}
															beforeUpload={() => false}
															accept=".pdf, .doc, .docx"
															disabled={uploadMutation.isPending}
															onRemove={() => {
																form.setFieldValue(
																	fieldName(['slikFile']),
																	null,
																);
																return false;
															}}
															onChange={async ({ file }) => {
																const fileid = Date.now().toString();
																const filepath = `customer/${mainInformation!.customerId}/${prospectId}/slik-file`;

																try {
																	message.open({
																		type: 'loading',
																		content: 'Uploading SLIK',
																		duration: 0,
																		key: fileid,
																	});

																	const url = await uploadMutation.mutateAsync({
																		file: file as unknown as File,
																		fileid,
																		filepath,
																	});

																	form.setFieldValue(fieldName(['slikFile']), {
																		format: 'jpeg',
																		id: fileid,
																		name: file.name,
																		path: filepath,
																		url,
																	});

																	message.destroy(fileid);
																} catch {
																	form.setFieldValue(
																		fieldName(['slikFile']),
																		null,
																	);
																	message.open({
																		type: 'error',
																		content: 'Failed Uploading SLIK',
																	});
																}
															}}
														>
															<UploadOutlined
																style={{
																	fontSize: 25,
																	color: '#BABABA',
																	display: 'block',
																}}
															/>
														</Upload.Dragger>
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item
														label="Kol Status"
														name={fieldName(['slikResult'])}
														rules={[formRule.required()]}
													>
														<Select
															{...slikStatus}
															options={
																slikStatus.options?.length
																	? slikStatus.options
																	: form?.getFieldValue(
																				fieldName(['slikResult']),
																		  )
																		? [
																				{
																					label:
																						'Kol ' +
																						form?.getFieldValue(
																							fieldName(['slikResult']),
																						),
																					value: form.getFieldValue(
																						fieldName(['slikResult']),
																					),
																				},
																			]
																		: []
															}
														/>
													</Form.Item>
												</Col>
											</Row>

											<Divider />
											<FleetTitle>SLIK Pengurus</FleetTitle>
											<Table
												scroll={{ x: 'max-content' }}
												style={{ marginBottom: 20 }}
												loading={isLoading}
												pagination={false}
												dataSource={
													managementSlikInformation?.map((manager, key) => {
														return {
															key,
															...manager,
														};
													}) ?? []
												}
												columns={[
													{
														title: 'View',
														width: 50,
														align: 'center',
														render: (_, record) => (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	openManagementModal(record.id);
																}}
															>
																<FileSearchOutlined />
															</a>
														),
													},
													{
														title: 'View Ideb',
														width: 100,
														align: 'center',
														render: (_, record) => (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	if (record.idebFile) {
																		openPreviewModal(
																			record.idebFile as FleetFile,
																		);
																	} else {
																		const file = await form.getFieldValue(
																			fieldName([
																				'managementSlikInformation',
																				record.id,
																				'idebFile',
																			]),
																		);
																		if (!file) return;
																		openPreviewModal(file as FleetFile);
																	}
																}}
															>
																<FileSearchOutlined
																	style={
																		!record.idebFile?.id ? { color: '#bababa' } : {}
																	}
																/>
															</a>
														),
													},
													{
														title: 'Upload',
														align: 'center',
														width: 50,
														render: (_, record, index) => (
															<Fragment>
																<Form.Item
																	name={fieldName([
																		'managementSlikInformation',
																		index,
																		'id',
																	])}
																	initialValue={record.id}
																	noStyle
																/>
																<Form.Item
																	// label="Upload Evidence"
																	name={fieldName([
																		'managementSlikInformation',
																		index,
																		'idebFile',
																	])}
																	rules={[formRule.required()]}
																	getValueProps={(value) => {
																		const file = value as FileData;
																		return {
																			fileList: file?.id
																				? ([
																						{
																							uid: file.id,
																							name: file.name,
																							thumbUrl: file.url,
																						},
																					] as UploadFile[])
																				: [],
																		};
																	}}
																	normalize={({
																		file,
																	}: {
																		file: UploadFile;
																	}) => {
																		return {
																			id: file.uid,
																			format: file.type,
																			name: file.name,
																			url: file.thumbUrl,
																		} as FileData;
																	}}
																	initialValue={record?.idebFile}
																>
																	<Upload.Dragger
																		listType="picture"
																		accept=".pdf, .doc, .docx"
																		disabled={uploadMutation.isPending}
																		beforeUpload={() => false}
																		onRemove={() => {
																			form.setFieldValue(
																				fieldName([
																					'managementSlikInformation',
																					index,
																					'idebFile',
																				]),
																				null,
																			);
																			return false;
																		}}
																		onChange={async ({ file }) => {
																			const fileid = Date.now().toString();
																			const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/management/${record.id}/ideb`;

																			try {
																				message.open({
																					type: 'loading',
																					content: `Uploading Ideb: ${record.name}`,
																					duration: 0,
																					key: fileid,
																				});

																				const url =
																					await uploadMutation.mutateAsync({
																						file: file as unknown as File,
																						fileid,
																						filepath,
																					});

																				form.setFieldValue(
																					fieldName([
																						'managementSlikInformation',
																						index,
																						'idebFile',
																					]),
																					{
																						format: 'jpeg',
																						id: fileid,
																						name: file.name,
																						path: filepath,
																						url,
																					},
																				);

																				message.destroy(fileid);
																			} catch {
																				form.setFieldValue(
																					fieldName([
																						'managementSlikInformation',
																						index,
																						'idebFile',
																					]),
																					null,
																				);
																				message.open({
																					type: 'error',
																					content: `Failed Uploading Ideb: ${record.name}`,
																				});
																			}
																		}}
																	>
																		<UploadOutlined
																			style={{
																				fontSize: 12,
																				display: 'block',
																			}}
																		/>
																	</Upload.Dragger>
																</Form.Item>
															</Fragment>
														),
													},
													{
														title: 'Nama Pengurus',
														render: (_, record) => record.name,
													},
													// {
													// 	title: 'KTP',
													// 	render: (_, record) => record.identityNumber ?? '-',
													// },
													{
														title: 'SLIK Result',
														render: (_, record, index) => {
															return (
																<Fragment>
																	<Form.Item
																		name={fieldName([
																			'managementSlikInformation',
																			index,
																			'id',
																		])}
																		initialValue={record.id}
																		noStyle
																	/>
																	<Form.Item
																		name={fieldName([
																			'managementSlikInformation',
																			index,
																			'slikResult',
																		])}
																		rules={[rule.required()]}
																	>
																		<Select
																			allowClear
																			size="small"
																			style={{ width: 80 }}
																			options={Array(5)
																				.fill(() => null)
																				.map((_, i) => {
																					const value = i + 1;
																					return {
																						label: value.toString(),
																						value,
																					};
																				})}
																		/>
																	</Form.Item>
																</Fragment>
															);
														},
													},
													// {
													// 	title: 'Plafon',
													// 	render: () => '-',
													// },
													// {
													// 	title: 'Baki Debet',
													// 	render: () => '-',
													// },
													// {
													// 	title: 'Tunggakan Pokok',
													// 	render: () => '-',
													// },
												]}
											/>

											<FleetTitle>SLIK Pemegang Saham</FleetTitle>

											<Table
												style={{ marginBottom: 20 }}
												scroll={{ x: 'max-content' }}
												loading={isLoading}
												pagination={false}
												dataSource={
													shareholderSlikInformation?.map(
														(shareholder, key) => {
															return {
																key,
																...shareholder,
															};
														},
													) ?? []
												}
												columns={[
													{
														title: 'View',
														width: 50,
														align: 'center',
														render: (_, record) => (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	openShareholderModal(record.id);
																}}
															>
																<FileSearchOutlined />
															</a>
														),
													},
													{
														title: 'View Ideb',
														width: 100,
														align: 'center',
														render: (_, record, index) => (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	if (record.idebFile) {
																		openPreviewModal(
																			record.idebFile as FleetFile,
																		);
																	} else {
																		const file = await form.getFieldValue(
																			fieldName([
																				'shareholderSlikInformation',
																				index,
																				'idebFile',
																			]),
																		);
																		openPreviewModal(file as FleetFile);
																	}
																}}
															>
																<FileSearchOutlined
																	style={
																		!record.idebFile?.id ? { color: '#bababa' } : {}
																	}
																/>
															</a>
														),
													},
													{
														title: 'Upload',
														align: 'center',
														width: 50,
														render: (_, record, index) => (
															<Fragment>
																<Form.Item
																	name={fieldName([
																		'shareholderSlikInformation',
																		index,
																		'id',
																	])}
																	initialValue={record.id}
																	noStyle
																/>
																<Form.Item
																	name={fieldName([
																		'shareholderSlikInformation',
																		index,
																		'idebFile',
																	])}
																	rules={[formRule.required()]}
																	getValueProps={(value) => {
																		const file = value as FileData;
																		return {
																			fileList: file?.id
																				? ([
																						{
																							uid: file.id,
																							name: file.name,
																							thumbUrl: file.url,
																						},
																					] as UploadFile[])
																				: [],
																		};
																	}}
																	normalize={({
																		file,
																	}: {
																		file: UploadFile;
																	}) => {
																		return {
																			id: file.uid,
																			format: file.type,
																			name: file.name,
																			url: file.thumbUrl,
																		} as FileData;
																	}}
																	initialValue={record?.idebFile}
																>
																	<Upload.Dragger
																		listType="picture"
																		accept=".pdf, .doc, .docx"
																		maxCount={1}
																		disabled={uploadMutation.isPending}
																		beforeUpload={() => false}
																		onRemove={() => {
																			form.setFieldValue(
																				fieldName([
																					'shareholderSlikInformation',
																					index,
																					'idebFile',
																				]),
																				null,
																			);
																			return false;
																		}}
																		onChange={async ({ file }) => {
																			const fileid = Date.now().toString();
																			const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/shareholder/${record.id}/ideb`;

																			try {
																				message.open({
																					type: 'loading',
																					content: `Uploading Ideb Pemegang Saham: ${record.name}`,
																					duration: 0,
																					key: fileid,
																				});

																				const url =
																					await uploadMutation.mutateAsync({
																						file: file as unknown as File,
																						fileid,
																						filepath,
																					});

																				form.setFieldValue(
																					fieldName([
																						'shareholderSlikInformation',
																						index,
																						'idebFile',
																					]),
																					{
																						format: 'jpeg',
																						id: fileid,
																						name: file.name,
																						path: filepath,
																						url,
																					},
																				);

																				message.destroy(fileid);
																			} catch {
																				form.setFieldValue(
																					fieldName([
																						'shareholderSlikInformation',
																						index,
																						'idebFile',
																					]),
																					null,
																				);
																				message.open({
																					type: 'error',
																					content: `Failed Uploading Ideb Pemegang Saham: ${record.name}`,
																				});
																			}
																		}}
																	>
																		<UploadOutlined
																			style={{
																				fontSize: 12,
																				display: 'block',
																			}}
																		/>
																	</Upload.Dragger>
																</Form.Item>
															</Fragment>
														),
													},
													{
														title: 'Nama Pemegang Saham',
														onCell: () => ({
															style: {
																whiteSpace: 'nowrap',
															},
														}),
														render: (_, record) => record.name,
													},
													// {
													// 	title: 'KTP',
													// 	render: (_, record) => record.identityNumber ?? '-',
													// },
													{
														title: 'SLIK Result',
														render: (_, record, index) => {
															return (
																<Fragment>
																	<Form.Item
																		name={fieldName([
																			'shareholderSlikInformation',
																			index,
																			'id',
																		])}
																		initialValue={record.id}
																		noStyle
																	/>
																	<Form.Item
																		name={fieldName([
																			'shareholderSlikInformation',
																			index,
																			'slikResult',
																		])}
																		rules={[rule.required()]}
																	>
																		<Select
																			allowClear
																			size="small"
																			style={{ width: 80 }}
																			options={Array(5)
																				.fill(() => null)
																				.map((_, i) => {
																					const value = i + 1;
																					return {
																						label: value.toString(),
																						value,
																					};
																				})}
																		/>
																	</Form.Item>
																</Fragment>
															);
														},
													},
													// {
													// 	title: 'Plafon',
													// 	render: () => '-',
													// },
													// {
													// 	title: 'Baki Debet',
													// 	render: () => '-',
													// },
													// {
													// 	title: 'Tunggakan Pokok',
													// 	render: () => '-',
													// },
												]}
											/>
										</Fragment>
									)}

									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												name="notes"
												label="Catatan"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>
									<Flex justify="space-between">
										<Button
											type="default"
											loading={submitResult.isPending}
											style={{
												width: 120,
												display: 'block',
												marginTop: 60,
											}}
											onClick={() => {
												if (slikCheckActiveTab === 'shareholder') {
													setSlikCheckActiveTab('manager');
												} else {
													setActiveTab('Internal Checking');
													window.scrollTo({
														top: 0,
														left: 0,
													});
												}
											}}
										>
											BACK
										</Button>
										<Button
											type="primary"
											loading={
												submitResult.isPending || uploadMutation.isPending
											}
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
												marginTop: 60,
											}}
											onClick={() => {
												if (slikCheckActiveTab === 'manager') {
													form
														?.validateFields(
															[
																'slikFile',
																'slikResult',
																'managementSlikInformation',
															],
															{ recursive: true },
														)
														.then(() => {
															setSlikCheckActiveTab('shareholder');
														})
														.catch((err) => {
															console.log('err', err);
														});
												} else {
													form
														?.validateFields(
															[
																'slikFile',
																'slikResult',
																'managementSlikInformation',
																'shareholderSlikInformation',
															],
															{
																recursive: true,
															},
														)
														.then(() => {
															setActiveTab('Eksternal Checking');
															window.scrollTo({
																top: 0,
																left: 0,
															});
														})
														.catch((err) => {
															console.log('err', err);
														});
												}
											}}
										>
											NEXT
										</Button>
									</Flex>
								</Fragment>
							) : !isLoadingSlikRobotikResult ? (
								<Fragment>
									<AnalisaSLIKFormRobotik
										setFinalSelectedDetail={(selectedDetail: {
											company: number[];
											shareholder: {
												referenceNumber: string;
												secondIndexes: number[];
											}[];
										}) => {
											form.setFieldValue(
												fieldName(['selectedCompanyIndexes']),
												selectedDetail.company,
											);
											form.setFieldValue(
												fieldName(['selectedShareholderIndexes']),
												selectedDetail.shareholder,
											);
										}}
										shareholderSlikInformation={
											shareholderSlikInformation ?? []
										}
										managementSlikInformation={managementSlikInformation ?? []}
										data={slikRobotikDetail?.data ?? []}
										result={slikRobotikResult?.data.details}
										setSlikResult={(slikResultRobotik) => {
											form.setFieldValue(
												fieldName(['slikResult']),
												slikResultRobotik,
											);
										}}
									/>
									<Divider />

									<Form.Item
										label="SLIK Robotik"
										name="isSlikRobotik"
										initialValue={!showSlikManual}
										noStyle
										shouldUpdate
									/>

									<Form.Item
										label="Kol Status"
										name={fieldName(['slikResult'])}
										rules={[formRule.required()]}
										noStyle
										shouldUpdate
									/>

									<Form.Item
										label="Selected Company Indexes"
										name={fieldName(['selectedCompanyIndexes'])}
										noStyle
										shouldUpdate
									/>

									<Form.Item
										label="Selected Shareholder Indexes"
										name={fieldName(['selectedShareholderIndexes'])}
										noStyle
										shouldUpdate
									/>

									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												name="notes"
												label="Catatan"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>
									<Flex justify="space-between">
										<Button
											type="default"
											loading={submitResult.isPending}
											style={{
												width: 120,
												display: 'block',
												marginTop: 60,
											}}
											onClick={() => {
												setActiveTab('Internal Checking');
												window.scrollTo({
													top: 0,
													left: 0,
												});
											}}
										>
											Back
										</Button>
										<Button
											type="primary"
											loading={
												submitResult.isPending || uploadMutation.isPending
											}
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
												marginTop: 60,
											}}
											onClick={() => {
												form
													?.validateFields(['notes'])
													.then(() => {
														setActiveTab('Eksternal Checking');
														window.scrollTo({
															top: 0,
															left: 0,
														});
													})
													.catch((err) => {
														console.log('err', err);
													});
											}}
										>
											NEXT
										</Button>
									</Flex>
								</Fragment>
							) : (
								<Flex
									justify="center"
									align="center"
								>
									<LoadingOutlined
										style={{
											marginTop: 160,
											fontSize: 100,
											color: '#1557BC',
											display: 'block',
										}}
									/>
								</Flex>
							),
						},
						{
							label: 'Eksternal Checking',
							disabled: true,
							key: 'Eksternal Checking',
							children: (
								<div style={{ marginBottom: 20 }}>
									{creditExternalChecks?.map((check, groupIndex) => {
										return (
											<div
												key={groupIndex}
												style={{ marginBottom: 20 }}
											>
												<FleetTitle>
													{FAQ_TITLE[
														check.category as keyof typeof FAQ_TITLE
													] ?? check.category}
												</FleetTitle>
												{check.creditExternalQuestions.map(
													({ question, answer, evidenceFile }) => {
														if (question.id === 11) {
															return (
																<Fragment key={question.id}>
																	<Divider />
																	<Row gutter={[40, 20]}>
																		<Col span={24}>
																			<Form.Item
																				name={fieldName([
																					'answers',
																					question.id,
																					'id',
																				])}
																				initialValue={question.id}
																				noStyle
																			/>
																			<Form.Item
																				name={fieldName([
																					'answers',
																					question.id,
																					'evidenceFile',
																				])}
																				required={
																					pepPengurus === 'Ya' ||
																					pepShareholder === 'Ya'
																				}
																				label={question.value}
																				labelCol={{
																					span: 16,
																					style: {
																						whiteSpace: 'normal',
																						alignSelf: 'center',
																					},
																				}}
																				rules={
																					pepPengurus === 'Ya' ||
																					pepShareholder === 'Ya'
																						? [formRule.required()]
																						: []
																				}
																				getValueProps={(value) => {
																					const file = value as FileData;
																					return {
																						fileList: file?.id
																							? ([
																									{
																										uid: file.id,
																										name: file.name,
																										thumbUrl: file.url,
																									},
																								] as UploadFile[])
																							: [],
																					};
																				}}
																				normalize={({
																					file,
																				}: {
																					file: UploadFile;
																				}) => {
																					return {
																						id: file.uid,
																						format: file.type,
																						name: file.name,
																						url: file.thumbUrl,
																					} as FileData;
																				}}
																				initialValue={evidenceFile}
																			>
																				<Upload.Dragger
																					listType="picture"
																					accept=".pdf,.jpg,.jpeg,.png"
																					maxCount={1}
																					beforeUpload={() => false}
																					onRemove={() => {
																						form.setFieldValue(
																							fieldName([
																								'answers',
																								question.id,
																								'evidenceFile',
																							]),
																							null,
																						);
																						return false;
																					}}
																					onChange={async ({ file }) => {
																						const fileid =
																							Date.now().toString();
																						const filepath = `customer/${mainInformation!.customerId}/${prospectId}/external-checking`;

																						try {
																							message.open({
																								type: 'loading',
																								content: `Uploading Evidence: ${question.value}`,
																								duration: 0,
																								key: fileid,
																							});

																							const url =
																								await uploadMutation.mutateAsync(
																									{
																										file: file as unknown as File,
																										fileid,
																										filepath,
																									},
																								);

																							form?.setFieldValue(
																								[
																									'answers',
																									question.id,
																									'evidenceFile',
																								],
																								{
																									id: fileid,
																									name: file?.name,
																									path: filepath,
																									format: 'jpeg',
																									url,
																								} as FilePayload,
																							);

																							message.destroy(fileid);
																						} catch {
																							form.setFieldValue(
																								fieldName([
																									'answers',
																									question.id,
																									'evidenceFile',
																								]),
																								null,
																							);
																							message.open({
																								type: 'error',
																								content: `Failed Uploading Evidence: ${question.value}`,
																							});
																						}
																					}}
																				>
																					<UploadOutlined
																						style={{
																							fontSize: 25,
																							color: '#BABABA',
																							display: 'block',
																						}}
																					/>
																				</Upload.Dragger>
																			</Form.Item>
																		</Col>
																	</Row>
																</Fragment>
															);
														}
														return (
															<Fragment key={question.id}>
																<Divider />
																<Row gutter={[40, 20]}>
																	<Col span={14}>
																		<Form.Item
																			name={fieldName([
																				'answers',
																				question.id,
																				'id',
																			])}
																			initialValue={question.id}
																			noStyle
																		/>
																		<Form.Item
																			name={fieldName([
																				'answers',
																				question.id,
																				'answer',
																			])}
																			label={question.value}
																			rules={[formRule.required()]}
																			labelCol={{
																				span: 16,
																				style: {
																					whiteSpace: 'normal',
																				},
																			}}
																			initialValue={answer.value}
																		>
																			<Radio.Group style={{ display: 'flex' }}>
																				<Radio
																					value={'Ya'}
																					style={{ lineHeight: '45px' }}
																				>
																					Ya
																				</Radio>
																				<Radio
																					value={'Tidak'}
																					style={{ lineHeight: '45px' }}
																				>
																					Tidak
																				</Radio>
																			</Radio.Group>
																		</Form.Item>
																	</Col>
																	<Col span={10}>
																		<Form.Item
																			label="Upload Evidences"
																			name={fieldName([
																				'answers',
																				question.id,
																				'evidenceFile',
																			])}
																			rules={[formRule.required()]}
																			getValueProps={(value) => {
																				const file = value as FileData;
																				return {
																					fileList: file?.id
																						? ([
																								{
																									uid: file.id,
																									name: file.name,
																									thumbUrl: file.url,
																								},
																							] as UploadFile[])
																						: [],
																				};
																			}}
																			normalize={({
																				file,
																			}: {
																				file: UploadFile;
																			}) => {
																				return {
																					id: file.uid,
																					format: file.type,
																					name: file.name,
																					url: file.thumbUrl,
																				} as FileData;
																			}}
																			initialValue={evidenceFile}
																		>
																			<Upload.Dragger
																				listType="picture"
																				accept=".pdf,.jpg,.jpeg,.png"
																				disabled={uploadMutation.isPending}
																				maxCount={1}
																				beforeUpload={() => false}
																				onRemove={() => {
																					form.setFieldValue(
																						fieldName([
																							'answers',
																							question.id,
																							'evidenceFile',
																						]),
																						null,
																					);
																					return false;
																				}}
																				onChange={async ({ file }) => {
																					const fileid = Date.now().toString();
																					const filepath = `customer/${mainInformation!.customerId}/${prospectId}/external-checking`;
																					try {
																						message.open({
																							type: 'loading',
																							content: `Uploading Evidence: ${question.value}`,
																							duration: 0,
																							key: fileid,
																						});
																						const url =
																							await uploadMutation.mutateAsync({
																								file: file as unknown as File,
																								fileid,
																								filepath,
																							});

																						form?.setFieldValue(
																							[
																								'answers',
																								question.id,
																								'evidenceFile',
																							],
																							{
																								format: 'jpeg',
																								id: fileid,
																								name: file?.name,
																								path: filepath,
																								url,
																							} as FilePayload,
																						);

																						message.destroy(fileid);
																					} catch {
																						form.setFieldValue(
																							fieldName([
																								'answers',
																								question.id,
																								'evidenceFile',
																							]),
																							null,
																						);
																						message.open({
																							type: 'error',
																							content: `Failed Uploading Evidence: ${question.value}`,
																						});
																					}
																				}}
																			>
																				<UploadOutlined
																					style={{
																						fontSize: 25,
																						color: '#BABABA',
																						display: 'block',
																					}}
																				/>
																			</Upload.Dragger>
																		</Form.Item>
																	</Col>
																</Row>
															</Fragment>
														);
													},
												)}
												<Divider />
												<Row gutter={[40, 20]}>
													<Col span={24}>
														<Form.Item
															name={MAP_ANALYSIS[check.category]}
															label={`Analisa ${getAnalysisLabel(check.category)}`}
															labelCol={{
																span: 6,
																style: {
																	alignSelf: 'center',
																},
															}}
															wrapperCol={{
																style: {
																	marginLeft: -10,
																},
															}}
															rules={[formRule.required()]}
														>
															<Input.TextArea rows={4} />
														</Form.Item>
													</Col>
												</Row>
											</div>
										);
									})}
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												label="Apakah ada pemberitaan negative?"
												labelCol={{
													span: 6,
													style: {
														whiteSpace: 'normal',
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>
									<Flex justify="space-between">
										<Button
											type="default"
											loading={submitResult.isPending}
											style={{
												width: 120,
												display: 'block',
												marginTop: 60,
											}}
											onClick={() => {
												setActiveTab('SLIK Checking');
												window.scrollTo({
													top: 0,
													left: 0,
												});
											}}
										>
											BACK
										</Button>
										<Button
											type="primary"
											// htmlType="submit"
											loading={
												submitResult.isPending || uploadMutation.isPending
											}
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
												marginTop: 60,
											}}
											onClick={async () => {
												await form
													?.validateFields()
													.then(() => {
														form?.submit();
													})
													.catch((err) => {
														console.log('err', err);
													});
											}}
										>
											SAVE
										</Button>
									</Flex>
								</div>
							),
						},
					]}
					style={{
						marginBottom: 20,
					}}
				/>
			</Form>

			<FleetModal
				open={successModal}
				cancelText="BACK"
				okText="NEXT"
				onOk={() => {
					setSuccessModal(false);
					navigate('CCAAnalisaSLIKPage');
				}}
				onCancel={() => {
					setSuccessModal(false);
				}}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses</div>}
					// subTitle="Pengecekan customer berhasil disimpan. Silahkan hubungi FBDH jika tidak di proses lebih dari 3 hari"
				/>
			</FleetModal>

			<RejectModal
				modal={rejectModal}
				onClose={() => setRejectModal(false)}
			/>

			<NegativeModal
				modal={negativeModal}
				onClose={() => setNegativeModal(false)}
			/>
		</Fragment>
	);
};

export default CCAAnalisaSLIKForm;
